import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export interface DefaultTask {
  id: string;
  name: string;
  description: string;
  due_in_days: number;
}

interface OnboardingTaskSetupProps {
  tasks: DefaultTask[];
  onTasksChange: (tasks: DefaultTask[]) => void;
  onComplete: () => void;
  onSkip: () => void;
}

const INTERVAL_OPTIONS = [
  { value: 1, label: '1 Tag' },
  { value: 3, label: '3 Tage' },
  { value: 7, label: '7 Tage (1 Woche)' },
  { value: 14, label: '14 Tage (2 Wochen)' },
  { value: 30, label: '30 Tage (1 Monat)' },
  { value: 90, label: '90 Tage (3 Monate)' },
];

const DEFAULT_TASKS: Omit<DefaultTask, 'id'>[] = [
  {
    name: 'Tägliche Kontaktaufnahme',
    description: 'Kontakt mit Kunden aufnehmen und Angebote verfolgen',
    due_in_days: 1
  },
  {
    name: 'Wöchentliche Bestandsaufnahme',
    description: 'Status aller laufenden Projekte überprüfen',
    due_in_days: 7
  },
  {
    name: 'Monatlicher Bericht',
    description: 'Monatlichen Verkaufsbericht erstellen und analysieren',
    due_in_days: 30
  },
  {
    name: 'Quartalsziele überprüfen',
    description: 'Quartalsziele und KPIs überprüfen und anpassen',
    due_in_days: 90
  }
];

const OnboardingTaskSetup: React.FC<OnboardingTaskSetupProps> = ({
  tasks,
  onTasksChange,
  onComplete,
  onSkip
}) => {
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    due_in_days: 7
  });
  const [showAddForm, setShowAddForm] = useState(false);

  React.useEffect(() => {
    // Initialize with default tasks if no tasks are provided
    if (tasks.length === 0) {
      const defaultTasks = DEFAULT_TASKS.map(task => ({
        ...task,
        id: crypto.randomUUID()
      }));
      onTasksChange(defaultTasks);
    }
  }, []);

  const addTask = () => {
    if (!newTask.name.trim()) {
      toast.error('Bitte geben Sie einen Aufgabennamen ein');
      return;
    }

    const task: DefaultTask = {
      id: crypto.randomUUID(),
      name: newTask.name.trim(),
      description: newTask.description.trim(),
      due_in_days: newTask.due_in_days
    };

    onTasksChange([...tasks, task]);
    setNewTask({ name: '', description: '', due_in_days: 7 });
    setShowAddForm(false);
    toast.success('Aufgabe hinzugefügt');
  };

  const removeTask = (taskId: string) => {
    onTasksChange(tasks.filter(task => task.id !== taskId));
    toast.success('Aufgabe entfernt');
  };

  const editTask = (taskId: string, updates: Partial<DefaultTask>) => {
    onTasksChange(tasks.map(task => 
      task.id === taskId ? { ...task, ...updates } : task
    ));
  };

  const getIntervalLabel = (days: number) => {
    const option = INTERVAL_OPTIONS.find(opt => opt.value === days);
    return option ? option.label : `${days} Tage`;
  };

  const getIntervalColor = (days: number) => {
    if (days === 1) return 'bg-red-100 text-red-700';
    if (days <= 7) return 'bg-orange-100 text-orange-700';
    if (days <= 30) return 'bg-blue-100 text-blue-700';
    return 'bg-green-100 text-green-700';
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2 text-2xl">
          <Clock className="h-6 w-6" />
          Standard-Aufgaben definieren
        </CardTitle>
        <CardDescription className="text-lg">
          Erstellen Sie wiederkehrende Aufgaben, die Ihr Team verwenden kann.
          Diese können später von allen Teammitgliedern für Kunden verwendet werden.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Existing Tasks */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Ihre Standard-Aufgaben</h3>
            <Badge variant="outline">
              {tasks.length} Aufgabe{tasks.length !== 1 ? 'n' : ''}
            </Badge>
          </div>
          
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Noch keine Aufgaben definiert</p>
              <p className="text-sm">Fügen Sie Standard-Aufgaben für Ihr Team hinzu</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {tasks.map((task) => (
                <Card key={task.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          value={task.name}
                          onChange={(e) => editTask(task.id, { name: e.target.value })}
                          className="font-medium border-0 p-0 h-auto bg-transparent"
                          placeholder="Aufgabenname"
                        />
                        <Badge className={getIntervalColor(task.due_in_days)}>
                          <Clock className="h-3 w-3 mr-1" />
                          {getIntervalLabel(task.due_in_days)}
                        </Badge>
                      </div>
                      <Textarea
                        value={task.description}
                        onChange={(e) => editTask(task.id, { description: e.target.value })}
                        placeholder="Beschreibung der Aufgabe..."
                        className="border-0 p-0 bg-transparent resize-none"
                        rows={2}
                      />
                      <Select
                        value={task.due_in_days.toString()}
                        onValueChange={(value) => editTask(task.id, { due_in_days: parseInt(value) })}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INTERVAL_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value.toString()}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeTask(task.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Add New Task Form */}
        {showAddForm ? (
          <Card className="p-4 border-dashed">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Aufgabenname</Label>
                <Input
                  value={newTask.name}
                  onChange={(e) => setNewTask(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Kundenkontrolle"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detaillierte Beschreibung der Aufgabe..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Wiederholungsintervall</Label>
                <Select
                  value={newTask.due_in_days.toString()}
                  onValueChange={(value) => setNewTask(prev => ({ ...prev, due_in_days: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVAL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={addTask} size="sm">
                  Hinzufügen
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowAddForm(false)} 
                  size="sm"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Button 
            variant="outline" 
            onClick={() => setShowAddForm(true)}
            className="w-full border-dashed"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neue Aufgabe hinzufügen
          </Button>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
          <Button onClick={onSkip} variant="outline" className="order-2 sm:order-1">
            Überspringen
          </Button>
          <Button 
            onClick={onComplete} 
            className="order-1 sm:order-2 sm:ml-auto gap-2"
            disabled={tasks.length === 0}
          >
            <CheckCircle2 className="h-4 w-4" />
            Weiter mit diesen Aufgaben
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingTaskSetup;
